// Spool List — flat tabular view of every spool across every visible
// job. One row per spool with progress, current stage, due date, and
// the assignee of the in-progress stage when one exists.
import { useMemo } from "react";
import type {
  JobDto,
  SpoolDto,
  SpoolStageDto,
  StageDefDto,
  TeamMemberDto,
} from "../../../../../../shared/types/production-schedule";
import { useActiveStages } from "../components/StagesProvider";
import {
  activeStages,
  currentStage,
  denseStages,
  inferWeldBayKey,
  isSpoolComplete,
} from "../lib/stageHelpers";
import { fmtDate, isoToday } from "../lib/dates";

interface Props {
  jobs: JobDto[];
  team: TeamMemberDto[];
  // When true (FilterBar's 'All Active' default), skip spools whose
  // progression stages are all complete — workshop wants 'still has
  // work' on screen. The parent already filters terminal-status
  // jobs server-side; this catches finished spools inside still-
  // active jobs. Read inside the view (not in the page) so the
  // fetched catalogue from StagesProvider drives isSpoolComplete.
  activeOnly?: boolean;
  onOpenJob(id: number, spoolId: number): void;
}

interface Row {
  job: JobDto;
  spool: SpoolDto;
  cur: SpoolStageDto | null;
  pct: number;
  hours: number;
  active: number;
  done: number;
}

function rowKey(r: Row): string {
  return `${r.job.id}-${r.spool.id}`;
}

export function SpoolListView({ jobs, team, activeOnly, onOpenJob }: Props) {
  const today = isoToday();
  const catalogue = useActiveStages();
  const stageByKey = useMemo(() => {
    const m = new Map<string, StageDefDto>();
    for (const s of catalogue) m.set(s.stageKey, s);
    return m;
  }, [catalogue]);
  const teamById = useMemo(() => {
    const m = new Map<number, TeamMemberDto>();
    for (const t of team) m.set(t.id, t);
    return m;
  }, [team]);

  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    for (const j of jobs) {
      for (const sp of j.spools) {
        if (activeOnly && isSpoolComplete({ spool: sp, catalogue, jobBay: j.bay })) continue;
        const bay = inferWeldBayKey(sp.stages, j.bay);
        const dense = denseStages(sp.stages, bay, undefined, catalogue);
        const cur = currentStage(dense);
        const active = activeStages(dense);
        const done = active.filter((s) => s.status === "complete").length;
        const pct = active.length > 0 ? Math.round((done / active.length) * 100) : 0;
        const hours = active.reduce((a, s) => a + (s.hours || 0), 0);
        out.push({ job: j, spool: sp, cur, pct, hours, active: active.length, done });
      }
    }
    return out;
  }, [jobs, catalogue, activeOnly]);

  if (rows.length === 0) {
    return (
      <div className="bsp-card" style={{ padding: 32, textAlign: "center", color: "var(--bsp-fg-muted)" }}>
        No spools yet.
      </div>
    );
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table className="list-table">
        <thead>
          <tr>
            <th>Job</th>
            <th>Spool</th>
            <th>Description</th>
            <th>Current Stage</th>
            <th style={{ textAlign: "right" }}>Hrs</th>
            <th style={{ textAlign: "right" }}>Stages</th>
            <th style={{ minWidth: 140 }}>Progress</th>
            <th>Assignee</th>
            <th>Due</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const def = r.cur ? stageByKey.get(r.cur.stageKey) : null;
            const overdue = r.spool.dueDate && r.spool.dueDate < today && r.pct < 100;
            const assignee = r.cur?.assigneeId ? teamById.get(r.cur.assigneeId) : null;
            return (
              <tr
                key={rowKey(r)}
                onClick={() => onOpenJob(r.job.id, r.spool.id)}
                style={{ cursor: "pointer" }}
              >
                <td style={{ fontFamily: "var(--bsp-font-mono)", fontWeight: 700, color: "var(--bsp-blue)" }}>
                  {r.job.jobNumber}
                </td>
                <td style={{ fontFamily: "var(--bsp-font-mono)", fontWeight: 700 }}>{r.spool.num}</td>
                <td style={{ color: "var(--bsp-fg)", fontSize: 12 }}>
                  {r.spool.description || <span style={{ color: "var(--bsp-fg-subtle)" }}>—</span>}
                  <div style={{ fontSize: 10, color: "var(--bsp-fg-muted)" }}>{r.job.name}</div>
                </td>
                <td>
                  {def ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11 }}>
                      <span style={{
                        display: "inline-block",
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: def.color,
                      }} />
                      {def.name}
                    </span>
                  ) : (
                    <span style={{ color: "var(--bsp-green-fg)", fontWeight: 600, fontSize: 11 }}>✓ Done</span>
                  )}
                </td>
                <td style={{ fontFamily: "var(--bsp-font-mono)", textAlign: "right" }}>{r.hours.toFixed(1)}h</td>
                <td style={{ fontFamily: "var(--bsp-font-mono)", textAlign: "right" }}>{r.done}/{r.active}</td>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{
                      flex: 1,
                      height: 4,
                      background: "var(--bsp-surface-muted)",
                      border: "1px solid var(--bsp-border)",
                      borderRadius: 3,
                      overflow: "hidden",
                      minWidth: 70,
                    }}>
                      <div style={{
                        width: `${r.pct}%`,
                        height: "100%",
                        background: "linear-gradient(90deg,var(--bsp-blue),var(--bsp-green))",
                      }} />
                    </div>
                    <span style={{ fontFamily: "var(--bsp-font-mono)", fontWeight: 700, fontSize: 11, minWidth: 30, textAlign: "right" }}>{r.pct}%</span>
                  </div>
                </td>
                <td>
                  {assignee ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        background: assignee.color,
                        color: "#fff",
                        fontSize: 9,
                        fontWeight: 700,
                      }}>{assignee.initials}</span>
                      <span style={{ fontSize: 12 }}>{assignee.name}</span>
                    </span>
                  ) : (
                    <span style={{ color: "var(--bsp-fg-subtle)" }}>—</span>
                  )}
                </td>
                <td style={{
                  fontFamily: "var(--bsp-font-mono)",
                  fontSize: 11,
                  color: overdue ? "var(--bsp-red-fg,#B3261E)" : "var(--bsp-fg-muted)",
                  fontWeight: overdue ? 700 : 400,
                }}>
                  {fmtDate(r.spool.dueDate)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
